import { useEffect, useRef, useState, RefObject } from "react";
import { Slider } from "@/ui/components/ui/slider";

/**
 * Slider granularity. The rAF loop quantises to this before writing state, so
 * the two can never disagree — a finer read would re-render without moving the
 * thumb a single pixel.
 */
const SCRUB_STEP = 0.1;

interface VideoScrubberProps {
  videoRef: RefObject<HTMLVideoElement>;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  onSeek: (value: number[]) => void;
  onSeekComplete: (value: number[]) => void;
  formatTime: (seconds: number) => string;
}

/**
 * Isolated scrubber subtree (thumb + current/total labels). Extracted out of
 * VideoPreviewEnhanced so its rAF-cadence position updates re-render ONLY this
 * tiny component — never the parent, which already re-renders ~30×/sec from the
 * timeline context and must not get a ~60fps setState piled on top.
 *
 * The rAF loop is DISPLAY-ONLY: it reads `videoRef.current.currentTime` and does
 * nothing else. It never writes `video.currentTime`, never calls setSelectedTime,
 * and never touches the playback-write / seek-detector / live-clamp machinery in
 * the parent — so the playback↔cursor feedback loop is untouched.
 */
export const VideoScrubber = ({
  videoRef,
  isPlaying,
  currentTime,
  duration,
  onSeek,
  onSeekComplete,
  formatTime,
}: VideoScrubberProps) => {
  // Local display position, advanced by the rAF loop while playing.
  const [displayTime, setDisplayTime] = useState(currentTime);

  // Non-null only while the user is actively dragging the thumb. The dragged
  // value wins over the rAF value so the loop can't fight the drag.
  const [dragValue, setDragValue] = useState<number | null>(null);
  const draggingRef = useRef(false);
  const rafRef = useRef<number | null>(null);

  // Smooth follow while playing: read the element's real currentTime every frame.
  // Runs only while playing; stops on pause/unmount and does one final exact read
  // so the thumb lands precisely on the paused frame.
  useEffect(() => {
    if (!isPlaying) return;
    // The <video> node is stable across a play session (source swaps reuse the
    // same element), so capturing it here is equivalent to reading it live and
    // lets the cleanup do its final exact read without a stale-ref warning.
    const el = videoRef.current;
    const tick = () => {
      if (el && !draggingRef.current) {
        // Quantise to the slider's own step before writing. Reading the raw
        // float produced a distinct value on EVERY frame, so the equality bail
        // below never fired and each frame re-rendered the slider — restyling
        // its range fill and thumb. Measured on /map that was ~190 DOM
        // mutations/sec from this component alone, at 120fps, for sub-pixel
        // movement nobody can see. Quantised, it settles to ~10 updates/sec at
        // 1× playback with pixel-identical output.
        const stepped = Math.round(el.currentTime / SCRUB_STEP) * SCRUB_STEP;
        setDisplayTime((prev) =>
          Math.abs(prev - stepped) < SCRUB_STEP / 2 ? prev : stepped,
        );
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    const arm = () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(tick);
    };
    arm();

    // Like every self-perpetuating rAF chain, this one ends for good if a single
    // callback never arrives — which is exactly what a backgrounded tab does to
    // its pending frames. Neither dep changes on the way back, so nothing
    // re-arms it: measured after a tab return, the element played on from 20.9s
    // to 25.9s while this thumb and its clock sat at 0:13. The video never
    // needed rAF, so the picture moved and only the CONTROLS looked dead.
    const onVisible = () => {
      if (!document.hidden) arm();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (el && !draggingRef.current) {
        setDisplayTime(el.currentTime);
      }
    };
  }, [isPlaying, videoRef]);

  // While NOT playing (and not dragging), mirror external position changes —
  // seek commit, skip buttons, video switch. `currentTime` (updated by the
  // parent's timeupdate handler) is the trigger; the element is the source of
  // truth so the paused thumb sits on the exact frame.
  useEffect(() => {
    if (isPlaying || draggingRef.current) return;
    const el = videoRef.current;
    setDisplayTime(el ? el.currentTime : currentTime);
  }, [currentTime, isPlaying, videoRef]);

  const handleValueChange = (value: number[]) => {
    draggingRef.current = true;
    setDragValue(value[0]);
    onSeek(value);
  };

  const handleValueCommit = (value: number[]) => {
    draggingRef.current = false;
    setDragValue(null);
    setDisplayTime(value[0]);
    onSeekComplete(value);
  };

  const shownTime = dragValue ?? displayTime;

  return (
    <div className="space-y-1">
      <Slider
        value={[shownTime]}
        onValueChange={handleValueChange}
        onValueCommit={handleValueCommit}
        max={duration || 100}
        step={SCRUB_STEP}
        className="w-full cursor-pointer"
      />
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{formatTime(shownTime)}</span>
        <span>{formatTime(duration)}</span>
      </div>
    </div>
  );
};
